<template>
  <div class="chat-messages" ref="rootEl">
    <div class="chat-date">{{ chatDate }}</div>

    <div
      v-for="(message, index) in messages"
      :key="index"
      :class="[
        'chat-message',
        message.type === 'question'
          ? 'chat-question'
          : message.type === 'answer'
            ? 'chat-answer'
            : 'chat-system'
      ]"
    >
      <div v-if="message.type === 'question'" class="question-bubble">
        <div class="question-text">{{ message.text }}</div>
        <div v-if="message.description" class="question-description">
          {{ message.description }}
        </div>
      </div>
      <div v-else-if="message.type === 'answer'" class="answer-bubble">
        <div class="answer-text">{{ message.text }}</div>
      </div>
      <YagiChatBotSystemBubble
        v-else-if="message.type === 'system'"
        :text="message.text"
        :skipped-questions="message.skippedQuestions"
        :edit-indices="message.skippedIndices"
        @edit="$emit('edit-skipped', $event)"
      />
      <YagiChatBotAiResultBubble
        v-else-if="message.type === 'ai-result'"
        :text="message.text"
        :active="message.active"
        @accept="$emit('ai-accept')"
        @regenerate="$emit('ai-regenerate')"
        @edit="$emit('ai-edit')"
      />
      <YagiChatBotSystemBubble
        v-else-if="message.type === 'company-info'"
        variant="company-info"
        :text="message.text"
        :company-data="message.companyData"
        :editable-fields="message.editableFields"
        @edit-fields="$emit('edit-company', $event)"
      />
    </div>

    <div v-if="thinkingMode" class="chat-message chat-system">
      <div class="thinking-bubble">
        <i class="el-icon-loading thinking-icon"></i>
        <span class="thinking-text">{{ thinkingLabel }}</span>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref } from 'vue'
import YagiChatBotAiResultBubble from '@/components/format-edit/yagi-chat-bot/YagiChatBotAiResultBubble.vue'
import YagiChatBotSystemBubble from '@/components/format-edit/yagi-chat-bot/YagiChatBotSystemBubble.vue'
import type { YagiChatMessage } from '@/utils/format-edit/yagiChatBot'

defineProps<{
  messages: YagiChatMessage[]
  chatDate: string
  thinkingMode: boolean
  thinkingLabel: string
}>()

defineEmits<{
  (e: 'edit-skipped', indices: number[]): void
  (e: 'edit-company', fields: string[]): void
  (e: 'ai-accept'): void
  (e: 'ai-regenerate'): void
  (e: 'ai-edit'): void
}>()

const rootEl = ref<HTMLElement | null>(null)

defineExpose({
  rootEl
})
</script>

<style lang="scss" scoped>
.chat-date {
  text-align: center;
  color: #999;
  font-size: 0.75rem;
  padding: 10px 0;
  background: #fff;
}

.chat-messages {
  flex: 1;
  overflow-y: auto;
  padding: 20px;
  padding-top: 0;
  background: #fff;

  &::-webkit-scrollbar {
    width: 6px;
  }

  &::-webkit-scrollbar-thumb {
    background: #ccc;
    border-radius: 3px;
  }
}

.chat-message {
  margin-bottom: 15px;
  animation: fade-in 0.3s ease-in;
}

@keyframes fade-in {
  from {
    opacity: 0;
    transform: translateY(10px);
  }

  to {
    opacity: 1;
    transform: translateY(0);
  }
}

.chat-question {
  display: flex;
  justify-content: flex-start;
}

.question-bubble {
  background: #eef5e8;
  padding: 12px 16px;
  border-radius: 12px;
  border-bottom-left-radius: 4px;
  max-width: 85%;

  .question-text {
    font-weight: 500;
    margin-bottom: 4px;
    line-height: 1.5;
  }

  .question-description {
    font-size: 0.85rem;
    color: #666;
    margin-top: 6px;
    line-height: 1.4;
  }
}

.thinking-bubble {
  background: #eef5e8;
  padding: 12px 16px;
  border-radius: 12px;
  border-bottom-left-radius: 4px;
  display: inline-flex;
  align-items: center;
  gap: 10px;
  max-width: 85%;

  .thinking-icon {
    color: #0f9934;
    font-size: 1.2rem;
  }

  .thinking-text {
    color: #2d6b3a;
    font-size: 0.92rem;
    font-weight: 500;
  }
}

.chat-answer {
  display: flex;
  justify-content: flex-end;
}

.answer-bubble {
  background: #0f9934;
  padding: 12px 16px;
  border-radius: 12px;
  border-bottom-right-radius: 4px;
  max-width: 85%;
  border: 1px solid #0f9934;
  color: #fff;
  font-weight: bold;

  .answer-text {
    white-space: pre-wrap;
    overflow-wrap: break-word;
    line-height: 1.5;
  }
}

.chat-system {
  display: flex;
  justify-content: flex-start;
}
</style>
